import { isArtifactKnowledgeSource } from '@/features/artifacts/artifact-knowledge.js';
import {
  isKnowledgeSourcePaused,
  type KnowledgeSource,
  type KnowledgeSourceIndexing,
} from '@/lib/api/workspace-knowledge.js';

/**
 * What a knowledge source's indexing state is called, in one place.
 *
 * Two screens say it now — the Knowledge tab's rows, and the switch in a place's or a folder's
 * settings, where somebody has just turned indexing on and wants to know whether it has happened
 * yet. Two copies of these words would drift, and the difference between "Indexing…" and
 * "Indexed 12" is exactly the thing a second copy gets wrong.
 */

/**
 * Presence-derived sync status, used only where real indexing status is unavailable. OPEN union —
 * an unknown status renders no badge rather than an `undefined` label.
 */
const SYNC_STATUS: Record<string, StatusBadge> = {
  disabled: { label: 'Disabled', variant: 'secondary' },
  registered: { label: 'Synced', variant: 'success' },
  pending: { label: 'Pending', variant: 'warning' },
};

export interface StatusBadge {
  label: string;
  variant: 'secondary' | 'success' | 'warning';
}

/**
 * Which side stopped, said plainly. All three cases badge as "Paused"; this line is what tells them
 * apart, so a shell pause the indexer has not picked up doesn't read like a settled one.
 */
export function pausedNote(
  source: KnowledgeSource,
  indexing: KnowledgeSourceIndexing,
): string {
  if (source.enabled) return 'Paused by the indexer';
  if (indexing.isActive) return 'The indexer has not confirmed the pause yet';
  return 'Hidden from the agent until resumed';
}

/**
 * Badge for a pass still in flight: the progress so far, plus the failures already seen — a failure does
 * not end the pass, so this must not claim the source is indexed.
 */
function runningBadge(indexing: KnowledgeSourceIndexing): StatusBadge {
  const { documentsTotal, documentsIndexed, documentsFailed } = indexing;
  /** Nothing counted, or a total the service did not report: say it is working, claim no numbers. */
  if (documentsTotal === null || documentsTotal === 0) {
    return { label: 'Indexing…', variant: 'secondary' };
  }
  if (documentsFailed > 0) {
    return {
      label: `Indexing ${documentsIndexed}/${documentsTotal} · ${documentsFailed} failed`,
      variant: 'warning',
    };
  }
  return {
    label: `Indexing ${documentsIndexed}/${documentsTotal}`,
    variant: 'secondary',
  };
}

/**
 * Badge once the pass has settled: what landed, and what did not. A folder can legitimately hold nothing
 * the extractor reads, which is a finished pass with no documents — not a broken one. A total the service
 * did not report leaves only the indexed count to show; claiming "no files" from that would invent it.
 * Files left out because the folder holds more content than one source indexes are counted last, and
 * they are why a source with no documents may still not be empty.
 */
function settledBadge(indexing: KnowledgeSourceIndexing): StatusBadge {
  const { documentsTotal, documentsIndexed, documentsFailed, filesOverBudget } =
    indexing;
  if (documentsFailed > 0 || filesOverBudget > 0) {
    const landed =
      documentsFailed > 0 && documentsTotal !== null
        ? `${documentsIndexed}/${documentsTotal}`
        : `${documentsIndexed}`;
    const parts = [`Indexed ${landed}`];
    if (documentsFailed > 0) parts.push(`${documentsFailed} failed`);
    if (filesOverBudget > 0) parts.push(`${filesOverBudget} over limit`);
    return { label: parts.join(' · '), variant: 'warning' };
  }
  if (documentsTotal === 0) {
    return { label: 'No files to index', variant: 'secondary' };
  }
  return { label: `Indexed ${documentsIndexed}`, variant: 'success' };
}

/**
 * Which badge a source shows. `pass` is sync's own answer about the current pass, so the choice between
 * "still working" and "finished" is read, not derived: the counters used to decide it, and they cannot
 * see a queued reindex — the row then kept advertising the previous complete result. `indexing: null`
 * still means the service could not reach sync at all, which is where `syncStatus` takes over.
 */
export function statusBadge(source: KnowledgeSource): StatusBadge | undefined {
  const indexing = source.indexing;
  /**
   * Nobody's access is on record for this source. It is checked FIRST because it is true whatever the
   * indexer says — including when the indexer could not be reached at all — and it is deliberately not
   * the same word as `Needs access` below: an unclaimed source is still indexed and still searchable,
   * and only its provenance is missing. Calling both of them broken would send somebody looking for a
   * search result that is, in fact, already there.
   */
  if (source.needsConnection) {
    return { label: 'Unclaimed', variant: 'warning' };
  }
  /**
   * A shelved workspace file has no `sync_status` worth reading: the service never stamps a sync source id
   * for this kind, so the field is `pending` on every row of it, forever. With no status block the honest
   * answer is that the state is unknown — sync could not be reached — not that the file is queued.
   */
  if (!indexing && isArtifactKnowledgeSource(source)) {
    return { label: 'Status unknown', variant: 'secondary' };
  }
  if (!indexing) return SYNC_STATUS[source.syncStatus];
  /**
   * The connection this source was registered through no longer resolves, so refreshing has stopped.
   * "Needs access" rather than "Awaiting reconnect": the way out is no longer for one person to reconnect
   * their own SharePoint — anybody with a connection of their own can adopt the source (canon §11).
   */
  if (indexing.needsReconnect)
    return { label: 'Needs access', variant: 'warning' };
  if (isKnowledgeSourcePaused(source))
    return { label: 'Paused', variant: 'secondary' };
  if (indexing.pass === 'settled') return settledBadge(indexing);
  /**
   * A queued pass has a job on sync's queue and has moved no document, so its counters still describe the
   * PREVIOUS pass — showing them would be the frozen-badge bug in a new outfit. It says work, no numbers.
   */
  if (indexing.pass === 'queued')
    return { label: 'Indexing…', variant: 'secondary' };
  return runningBadge(indexing);
}
